import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
} from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

export type AudioPlayerProps = {
  /** Presigned media URL. Changing this resets state to the new track. */
  src: string;
  /**
   * Stable identifier for persisting resume-from-position. Recommended:
   * `${chapter_id}:${asset_type}:${language}`. Position is saved every
   * ~5s and restored on mount when the same `progressKey` is loaded.
   */
  progressKey?: string;
  /** Optional slot above the controls -- e.g. a language switcher. */
  topSlot?: React.ReactNode;
  /** Called when the audio element fires an unrecoverable error. */
  onError?: () => void;
  /** Every `timeupdate`, with the current position and total duration. Lets the
   *  caller measure how much was actually played (see useMediaCompletion). */
  onPlaybackProgress?: (info: { currentTime: number; duration: number }) => void;
  className?: string;
};

const SAVE_EVERY_SECONDS = 5;
const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

/**
 * Brand-themed audio player with a waveform visualisation. Matches the v1
 * podcast player's feature set (play/pause, skip +-10s, seek, volume,
 * mute, playback rate, resume-from-position) but in the Pulse design
 * language (orange brand, calm motion, mobile-first controls).
 *
 * Waveform: Web Audio API analyser -> three layered sinusoidal traces
 * tinted with brand + warm-amber + warm-rose. The traces breathe even
 * when audio is silent (the audio amplitude only modulates them), so
 * the canvas never goes flat-line during quiet passages.
 *
 * Resume-from-position: localStorage under `staxed:audio-progress:<key>`.
 * Survives reloads; throttled to one write every ~5 seconds while
 * playing.
 */
export function AudioPlayer({ src, progressKey, topSlot, onError, onPlaybackProgress, className }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const lastSavedRef = useRef<number>(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);

  const storageKey = useMemo(
    () => (progressKey ? `staxed:audio-progress:${progressKey}` : null),
    [progressKey],
  );

  // ----------------------------------------------------------------------
  // Resume-from-position on mount / src change.
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!storageKey || !audioRef.current) return;
    const saved = parseFloat(localStorage.getItem(storageKey) ?? "");
    if (Number.isFinite(saved) && saved > 0) {
      // Wait until metadata is loaded so currentTime is honoured.
      const audio = audioRef.current;
      const apply = () => {
        if (audioRef.current) audioRef.current.currentTime = saved;
      };
      if (audio.readyState >= 1) apply();
      else audio.addEventListener("loadedmetadata", apply, { once: true });
    }
    // src change -> reset all derived state
    setIsPlaying(false);
    setCurrentTime(0);
    setIsLoading(true);
  }, [src, storageKey]);

  // ----------------------------------------------------------------------
  // Set up the Web Audio analyser on first play -- not before, because
  // some browsers block AudioContext creation until a user gesture.
  // ----------------------------------------------------------------------
  const ensureAudioGraph = useCallback(() => {
    if (audioCtxRef.current || !audioRef.current) return;
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      const source = ctx.createMediaElementSource(audioRef.current);
      source.connect(analyser);
      analyser.connect(ctx.destination);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
    } catch {
      // CORS-tainted media (no Access-Control-Allow-Origin on the
      // signed response) will throw here. We swallow + fall back to
      // a silent canvas -- the audio still plays.
    }
  }, []);

  const drawFrame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;

    // Trail background -- semi-transparent fill so previous frames
    // fade rather than snap, giving the wave a subtle motion blur.
    ctx.fillStyle = "rgba(0, 0, 0, 0)";
    ctx.clearRect(0, 0, w, h);

    let dataArray: Uint8Array<ArrayBuffer> | null = null;
    if (analyserRef.current) {
      const buf = analyserRef.current.frequencyBinCount;
      dataArray = new Uint8Array(new ArrayBuffer(buf));
      analyserRef.current.getByteFrequencyData(dataArray);
    }

    const points = 96;
    const centerY = h / 2;

    const drawWave = (offset: number, color: string, amp: number, freq: number, alpha: number) => {
      ctx.beginPath();
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = color;
      ctx.globalAlpha = alpha;

      for (let i = 0; i <= points; i++) {
        const x = (i / points) * w;
        const audioInfluence = dataArray
          ? dataArray[Math.floor((i / points) * dataArray.length)] / 255
          : 0;
        const wave = Math.sin(i * freq + offset) * amp * (0.35 + audioInfluence * 0.65);
        const y = centerY + wave;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    };

    const t = performance.now() * 0.001;
    // Pulse-aligned palette: brand orange + warm amber + soft coral.
    drawWave(t * 1.0,       "#F97316", 28, 0.10, 0.95); // pulse-500
    drawWave(t * 1.2 + 1.5, "#FB923C", 22, 0.12, 0.7);  // pulse-400
    drawWave(t * 1.4 + 3.0, "#FED7AA", 18, 0.14, 0.5);  // pulse-200

    animationRef.current = requestAnimationFrame(drawFrame);
  }, []);

  useEffect(() => {
    // Always run the canvas loop so the wave breathes even before first
    // play. The analyser branch only kicks in once the AudioContext exists.
    animationRef.current = requestAnimationFrame(drawFrame);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [drawFrame]);

  // High-DPI canvas sizing -- prevents blurry traces on retina.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.scale(dpr, dpr);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  // ----------------------------------------------------------------------
  // Audio handlers
  // ----------------------------------------------------------------------
  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      ensureAudioGraph();
      // Resume the AudioContext if it was suspended (autoplay policies).
      if (audioCtxRef.current?.state === "suspended") {
        void audioCtxRef.current.resume();
      }
      void audio.play().catch(() => onError?.());
    }
  }, [isPlaying, ensureAudioGraph, onError]);

  const skip = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(audio.currentTime + seconds, duration || audio.duration || 0));
  }, [duration]);

  const seek = useCallback((value: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value;
    setCurrentTime(value);
  }, []);

  const setVol = useCallback((v: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = v;
    setVolume(v);
    setIsMuted(v === 0);
  }, []);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isMuted) {
      const restore = volume > 0 ? volume : 0.6;
      audio.volume = restore;
      setVolume(restore);
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  const setRate = useCallback((rate: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = rate;
    setPlaybackRate(rate);
  }, []);

  // Keyboard shortcuts: Space=play/pause, J/Left=-10s, L/Right=+10s, M=mute.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      if (e.key === " " || e.code === "Space") { e.preventDefault(); togglePlay(); }
      else if (e.key === "ArrowLeft" || e.key === "j" || e.key === "J") skip(-10);
      else if (e.key === "ArrowRight" || e.key === "l" || e.key === "L") skip(10);
      else if (e.key === "m" || e.key === "M") toggleMute();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [togglePlay, skip, toggleMute]);

  // ----------------------------------------------------------------------
  // MediaSession: lock-screen + notification-shade + Bluetooth headset
  // controls. Without this, pressing the play button on AirPods or
  // pulling down the notification shade can't drive playback. Best
  // effort -- feature-detect and silently skip on unsupported browsers.
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;
    ms.setActionHandler("play",         () => togglePlay());
    ms.setActionHandler("pause",        () => togglePlay());
    ms.setActionHandler("seekbackward", (e) => skip(-(e.seekOffset ?? 10)));
    ms.setActionHandler("seekforward",  (e) => skip(e.seekOffset ?? 10));
    try {
      ms.setActionHandler("seekto", (e) => {
        if (typeof e.seekTime === "number") seek(e.seekTime);
      });
    } catch { /* older browsers */ }
    return () => {
      ["play", "pause", "seekbackward", "seekforward", "seekto"].forEach((a) => {
        try { ms.setActionHandler(a as MediaSessionAction, null); } catch { /* ignore */ }
      });
    };
  }, [togglePlay, skip, seek]);

  // Mirror playback state + position to MediaSession so the lock-screen
  // scrubber updates and the play/pause icon reflects reality.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
  }, [isPlaying]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    if (!duration || !Number.isFinite(duration)) return;
    try {
      navigator.mediaSession.setPositionState?.({
        duration,
        position: Math.min(currentTime, duration),
        playbackRate,
      });
    } catch { /* range errors are common during init; ignore */ }
  }, [currentTime, duration, playbackRate]);

  return (
    <div className={cn("w-full max-w-3xl mx-auto px-3 sm:px-4", className)}>
      {topSlot ? <div className="mb-5">{topSlot}</div> : null}

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
        className="relative rounded-2xl border border-border bg-surface overflow-hidden shadow-sm"
      >
        <audio
          ref={audioRef}
          src={src}
          preload="auto"
          crossOrigin="anonymous"
          onLoadedMetadata={(e) => {
            setDuration(e.currentTarget.duration);
            setIsLoading(false);
          }}
          onTimeUpdate={(e) => {
            const t = e.currentTarget.currentTime;
            setCurrentTime(t);
            onPlaybackProgress?.({ currentTime: t, duration: e.currentTarget.duration });
            if (storageKey && Math.floor(t) - lastSavedRef.current >= SAVE_EVERY_SECONDS) {
              localStorage.setItem(storageKey, String(t));
              lastSavedRef.current = Math.floor(t);
            }
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onPlaying={() => { setIsLoading(false); setIsBuffering(false); }}
          onWaiting={() => { if (!isLoading) setIsBuffering(true); }}
          onCanPlay={() => { setIsLoading(false); setIsBuffering(false); }}
          onEnded={() => {
            setIsPlaying(false);
            if (storageKey) localStorage.removeItem(storageKey);
          }}
          onError={() => { setIsLoading(false); onError?.(); }}
        />

        {/* Waveform canvas ------------------------------------------- */}
        <div className="relative aspect-[16/6] sm:aspect-[16/5] bg-gradient-to-b from-surface-2 to-surface-3 dark:from-bg dark:to-surface">
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
          {isLoading ? (
            <div className="absolute inset-0 grid place-items-center bg-bg/60 backdrop-blur-[2px]">
              <div className="flex items-center gap-2 text-sm text-text">
                <Loader2 className="h-4 w-4 animate-spin text-brand" />
                <span>Loading audio…</span>
              </div>
            </div>
          ) : null}
        </div>

        {/* Controls -------------------------------------------------- */}
        <div className="p-4 sm:p-5">
          <Seek
            current={currentTime}
            duration={duration}
            disabled={isLoading}
            onSeek={seek}
          />

          <div className="mt-4 flex items-center justify-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Skip back 10 seconds"
              onClick={() => skip(-10)}
              disabled={isLoading}
              className="h-10 w-10 sm:h-11 sm:w-11"
            >
              <SkipBack className="h-5 w-5" />
            </Button>
            <button
              type="button"
              aria-label={isPlaying ? "Pause" : "Play"}
              onClick={togglePlay}
              disabled={isLoading}
              className={cn(
                "grid place-items-center h-14 w-14 sm:h-16 sm:w-16 rounded-full",
                "bg-brand text-white shadow-md",
                "hover:scale-[1.04] active:scale-[0.97] transition-transform",
                "disabled:opacity-60 disabled:cursor-not-allowed",
              )}
            >
              {isPlaying ? (
                <Pause className="h-6 w-6 sm:h-7 sm:w-7" />
              ) : (
                <Play className="h-6 w-6 sm:h-7 sm:w-7 ml-0.5" />
              )}
            </button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Skip forward 10 seconds"
              onClick={() => skip(10)}
              disabled={isLoading}
              className="h-10 w-10 sm:h-11 sm:w-11"
            >
              <SkipForward className="h-5 w-5" />
            </Button>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <VolumeControl
              value={isMuted ? 0 : volume}
              isMuted={isMuted}
              disabled={isLoading}
              onChange={setVol}
              onToggleMute={toggleMute}
            />
            <RateControl value={playbackRate} onChange={setRate} disabled={isLoading} />
          </div>

          {isBuffering ? (
            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-text-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-brand" />
              <span>Buffering…</span>
            </div>
          ) : null}
        </div>
      </motion.div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Sub-controls -- native range inputs styled with Tailwind. Keeps the
// bundle lean (no radix-slider added just for this).
// --------------------------------------------------------------------------

function Seek({
  current,
  duration,
  disabled,
  onSeek,
}: {
  current: number;
  duration: number;
  disabled: boolean;
  onSeek: (v: number) => void;
}) {
  const max = duration || 1;
  return (
    <div>
      <input
        type="range"
        min={0}
        max={max}
        step={1}
        value={current}
        disabled={disabled}
        onChange={(e) => onSeek(parseFloat(e.target.value))}
        aria-label="Seek"
        className={cn(rangeClass, "w-full")}
        style={{
          // Filled portion of the track -- two-stop gradient.
          backgroundImage: `linear-gradient(to right, rgb(var(--brand)) 0%, rgb(var(--brand)) ${
            (current / max) * 100
          }%, rgb(var(--surface-3)) ${(current / max) * 100}%, rgb(var(--surface-3)) 100%)`,
        }}
      />
      <div className="mt-1.5 flex justify-between text-[11px] sm:text-xs text-text-muted tabular-nums">
        <span>{formatTime(current)}</span>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
}

function VolumeControl({
  value,
  isMuted,
  disabled,
  onChange,
  onToggleMute,
}: {
  value: number;
  isMuted: boolean;
  disabled: boolean;
  onChange: (v: number) => void;
  onToggleMute: () => void;
}) {
  return (
    <div className="flex items-center gap-2 sm:flex-1 sm:max-w-[220px]">
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggleMute}
        disabled={disabled}
        aria-label={isMuted ? "Unmute" : "Mute"}
      >
        {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      </Button>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        aria-label="Volume"
        className={cn(rangeClass, "flex-1")}
        style={{
          backgroundImage: `linear-gradient(to right, rgb(var(--brand)) 0%, rgb(var(--brand)) ${
            value * 100
          }%, rgb(var(--surface-3)) ${value * 100}%, rgb(var(--surface-3)) 100%)`,
        }}
      />
    </div>
  );
}

function RateControl({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap justify-center sm:justify-end">
      <span className="text-[11px] sm:text-xs text-text-muted mr-1">Speed</span>
      {PLAYBACK_RATES.map((r) => (
        <button
          key={r}
          type="button"
          onClick={() => onChange(r)}
          disabled={disabled}
          className={cn(
            "px-2 py-1 rounded-md text-xs tabular-nums transition-colors",
            value === r
              ? "bg-brand text-white"
              : "text-text-muted hover:text-text hover:bg-surface-2",
          )}
        >
          {r}x
        </button>
      ))}
    </div>
  );
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Tailwind class string for native range inputs -- track + thumb both
// themed to the Pulse palette. Centralised so seek + volume look the
// same. The webkit/moz pseudo-elements need explicit cross-vendor blocks.
const rangeClass = cn(
  "h-1.5 appearance-none rounded-full cursor-pointer outline-none bg-surface-3",
  "disabled:cursor-not-allowed disabled:opacity-50",
  // Webkit thumb
  "[&::-webkit-slider-thumb]:appearance-none",
  "[&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5",
  "[&::-webkit-slider-thumb]:rounded-full",
  "[&::-webkit-slider-thumb]:bg-brand",
  "[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white",
  "[&::-webkit-slider-thumb]:shadow",
  // Firefox thumb
  "[&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:w-3.5",
  "[&::-moz-range-thumb]:rounded-full",
  "[&::-moz-range-thumb]:bg-brand",
  "[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white",
  "[&::-moz-range-thumb]:shadow",
);
